import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Constants from 'expo-constants';
import { PipelineEnvironment } from 'utils/environment';
import { Platform } from 'utils/platform';
import { handleBaseQuery } from 'utils/request';

const cmsApiURL = () => {
  if (Platform.OS === 'web') {
    switch (Constants.expoConfig?.extra?.environment) {
      case PipelineEnvironment.CMS1:
        return Constants.expoConfig?.extra?.cmsM1Api;
      case PipelineEnvironment.CMS2:
        return Constants.expoConfig?.extra?.cmsM2Api;
      default:
        return Constants.expoConfig?.extra?.cmsApi;
    }
  }
  return Constants.expoConfig?.extra?.cmsApi;
};

export const cmsApiSlice = createApi({
  reducerPath: 'cmsApi',
  baseQuery: handleBaseQuery(fetchBaseQuery({ baseUrl: cmsApiURL() })),
  tagTypes: ['Program', 'Highlights', 'GlobalConfig', 'Home'],
  endpoints: (builder) => ({
    getHome: builder.query<CmsHome, string>({
      query: (id) => `/api/v1/BandplayHome/${id}/h`,
      providesTags: (result, error, arg) => [{ type: 'Home', id: arg }],
    }),
    getPrograms: builder.query<CmsApiProgramList, CmsApiProgramParams>({
      query: (params) => ({
        url: `/api/v1/BandplayProgram`,
        params: { ...params, limit: Platform.isTV ? 8 : params?.limit },
      }),
      transformResponse: (response: CmsApiProgramList): CmsApiProgramList => {
        if (response && Array.isArray(response.items)) {
          const modifiedItems = response.items.map(program => {
            const newProgram = { ...program };

            const modifyImageUrl = (imageObj: CmsImage | undefined): CmsImage | undefined => {
              if (!imageObj) {
                return undefined;
              }
              
              return {
                ...imageObj,
                url: imageObj.url.replace('.uol', ''),
              };
            };

            if (newProgram.imageHorizontal) {
              newProgram.imageHorizontal = modifyImageUrl(newProgram.imageHorizontal);
            }
            if (newProgram.imageVertical) {
              newProgram.imageVertical = <CmsImage>modifyImageUrl(newProgram.imageVertical);
            }
            if (newProgram.imageBg) {
              newProgram.imageBg = modifyImageUrl(newProgram.imageBg);
            }
            if (newProgram.imageBgHorizontal) {
              newProgram.imageBgHorizontal = modifyImageUrl(newProgram.imageBgHorizontal);
            }

            return newProgram;
          });

          return {
            ...response,
            items: modifiedItems,
          };
        }
        
        return response;
      },
    }),
    getProgramGroup: builder.query<any, string>({
      query: (id) => ({
        url: `/api/v1/BandplayProgramGroup`,
        params: { _id: id },
      }),
    }),
    getProgram: builder.query<CmsProgram, string>({
      query: (id) => `/api/v1/BandplayProgram/${id}`,
      transformResponse: (response: CmsProgram): CmsProgram => {
        const newProgramData = { ...response };

        const modifyImageUrlInObject = (imageObj: CmsImage | undefined): CmsImage | undefined => {
          if (!imageObj) {
            return undefined;
          }
          const newImage = { ...imageObj };
          newImage.url = newImage.url.replace('.uol', '');

          return newImage;
        };

        if (newProgramData.imageHorizontal) {
          newProgramData.imageHorizontal = modifyImageUrlInObject(newProgramData.imageHorizontal);
        }
        if (newProgramData.imageVertical) {
          newProgramData.imageVertical = modifyImageUrlInObject(newProgramData.imageVertical);
        }
        if (newProgramData.imageBg) {
          newProgramData.imageBg = modifyImageUrlInObject(newProgramData.imageBg);
        }
        if (newProgramData.imageBgHorizontal) {
          newProgramData.imageBgHorizontal = modifyImageUrlInObject(newProgramData.imageBgHorizontal);
        }

        return newProgramData;
      },
    }),
    getTag: builder.query<CmsTag, string>({
      query: (id) => `/api/v1/BandplayTags/${id}`,
    }),
    getMedias: builder.query<CmsApiMediaList, CmsApiMediaParams>({
      query: (params) => ({
        url: `/api/v1/BandplayMedia`,
        params: { limit: 60, ...params },
      }),
    }),
    getMedia: builder.query<CmsMedia, string>({
      query: (id) => `/api/v1/BandplayMedia/${id}`,
    }),
    getHighlights: builder.query<CmsApiHightlightList, CmsApiHightlightParams>({
      query: (params) => ({
        url: `/api/v1/BandplayHighlights`,
        params: { sort: 'order', ...params },
      }),
    }),
    getHelp: builder.query<CmsHelp, void>({
      query: () => `/api/v1/BandplayHelp/ajuda`,
    }),
    getGlobalConfig: builder.query<GlobalConfig, void>({
      query: () => `/api/v1/BandplayConfig/config`,
      providesTags: () => [{ type: 'GlobalConfig' }],
    }),
    getBandshopConfig: builder.query<BandshopResponse, void>({
      query: () => `/api/v1/BandplayOffers`,
    }),
  }),
});

export const {
  useGetHomeQuery,
  useGetProgramsQuery,
  useGetProgramQuery,
  useGetBandshopConfigQuery,
  useGetProgramGroupQuery,
  useGetTagQuery,
  useGetMediasQuery,
  useGetMediaQuery,
  useGetHighlightsQuery,
  useGetHelpQuery,
  useGetGlobalConfigQuery,
  useLazyGetProgramsQuery,
  useLazyGetHighlightsQuery,
  useLazyGetMediasQuery,
  useLazyGetProgramGroupQuery,
} = cmsApiSlice;
